import { useRef, useState } from "react";
import { X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { apiClient, createBillingIdempotencyKey } from "@/api/client";
import { theme } from "@/lib/theme";
import { cn } from "@/lib/utils";

interface CancelConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function CancelConfirmationModal({
  isOpen,
  onClose,
}: CancelConfirmationModalProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const idempotencyKey = useRef(createBillingIdempotencyKey("cancel-plan"));

  if (!isOpen) return null;

  const handleConfirm = async () => {
    setIsLoading(true);
    setError(null);

    try {
      await apiClient.cancelPlan(idempotencyKey.current);
      navigate("/paywall", { state: { message: "Your subscription has been canceled." } });
    } catch (err) {
      console.error("Failed to cancel subscription", err);
      setError(err instanceof Error ? err.message : "Failed to cancel subscription. Please try again.");
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 pt-20 md:pt-4 bg-black/50 backdrop-blur-sm">
      <div
        className={cn(
          "relative w-full max-w-md max-h-[calc(100vh-6rem)] md:max-h-[90vh] overflow-y-auto",
          theme.cards.base,
          "p-6 md:p-8",
        )}
      >
        <button
          onClick={onClose}
          disabled={isLoading}
          className="absolute top-4 right-4 p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          aria-label="Close"
        >
          <X className="w-5 h-5 text-gray-900 dark:text-white" />
        </button>

        <h2 className="text-2xl font-black text-gray-900 dark:text-white mb-4">Cancel Subscription</h2>

        <div className={cn("mb-6 p-4 rounded-lg", theme.borders.error, theme.colors.errorBg)}>
          <p className="text-sm font-semibold text-gray-900 dark:text-white mb-2">
            ⚠️ Are you sure you want to cancel your subscription?
          </p>
          <p className="text-sm text-gray-700 dark:text-gray-300">
            Canceling stops automatic renewal. Your account, podcast settings, transcripts, and chat history are not deleted by this action.
          </p>
          <p className="mt-2 text-sm font-semibold text-gray-900 dark:text-white">
            You can restart a subscription later.
          </p>
        </div>

        <div className={cn("mb-6 p-4 rounded-lg", theme.borders.neubrutalism, "bg-gray-50 dark:bg-gray-800/50")}>
          <p className="text-sm text-gray-700 dark:text-gray-300">
            Your access will continue until the end of your current billing period.
            You will not be charged again unless you restart your subscription.
          </p>
        </div>

        {error && (
          <div className={cn("mb-4 p-3 rounded-lg text-sm", theme.borders.error, theme.colors.errorBg, theme.text.error)}>
            {error}
          </div>
        )}

        <div className="flex gap-4">
          <button
            onClick={onClose}
            disabled={isLoading}
            className={cn(theme.buttons.secondary, "flex-1 px-6 py-3 rounded-xl", isLoading && "opacity-50 cursor-not-allowed")}
          >
            Keep Subscription
          </button>
          <button
            onClick={handleConfirm}
            disabled={isLoading}
            className={cn(
              theme.buttons.destructive,
              "flex-1 px-6 py-3 rounded-xl",
              isLoading && "opacity-50 cursor-not-allowed",
            )}
          >
            {isLoading ? "Canceling..." : "Yes, Cancel Subscription"}
          </button>
        </div>
      </div>
    </div>
  );
}
